import { Spinner } from "common-tailwind-components";
import { ChevronDown, ChevronRight, TriangleAlert, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { get_recording_analysis_report } from "../../api";
import type {
  AnalysisReport,
  AnalyzerStage,
  AnnotationReport,
  CleanupPass,
  CleanupReport,
  CleanupThresholds,
  InventedName,
  LevelReport,
  ObjectGroup,
  ObjectOverlap,
  ObjectsReport,
  OrphanNode,
  PruneReport,
  RecordingAnalysisReport,
  ResumedFrom,
  StageChunkingReport,
  StageCostReport,
  StageProvenance,
  StageRetryReport,
  ToolCallLog,
  VisionFailureKind,
  VocabReport,
} from "../../api/types";
import type { AnalysisReportKey } from "../../globals/i18n/shared";
import { formatDuration } from "../../utils";
import VirtualRecordingsContent, { hasSharedPartition } from "./VirtualRecordingsContent";
import { ANALYZER_STAGES, isLevelInherited, isStageInherited } from "./analyzerStages";
import type { ReportWarnings, Warning } from "./analysisReportWarnings";
import {
  businessObjectsPerHour,
  cleanedEventCount,
  computeReportWarnings,
  fmtStage,
  levelTotalS,
  nameReuse,
  orphanedDurationS,
  passInputCount,
  pct,
  perHour,
  sharePct,
  warnId,
  warningsFor,
} from "./analysisReportWarnings";

/** Bare figure: the currency is named once, in the column heading. */
const fmtCost = (usd: number) => usd.toFixed(4);
/** One decimal, dropped when it is zero: "1", not "1.0". */
const fmtAvg = (value: number) => value.toFixed(1).replace(/\.0$/, "");
/**
 * The score can be a hair below zero (rounding of a near-zero difference); plain
 * `.toFixed(2)` keeps the sign and prints "-0.00". Normalize anything that rounds
 * to zero back to a clean "0.00" so the sign does not read as meaningful.
 */
const fmtFragmentation = (value: number) => {
  const text = value.toFixed(2);
  return text === "-0.00" ? "0.00" : text;
};
/** A derived per-hour rate: an em dash when the recording has no measured length. */
const fmtRate = (value: number | null) => (value === null ? "—" : fmtAvg(value));
/**
 * A signed millisecond bound of a window, for the prose that quotes one. Signed
 * explicitly, and read off the report rather than described in words: the
 * concurrent window's near edge opens *before* the click, and a sentence saying
 * so in words would silently lie if that bound were ever retuned past zero.
 */
const fmtSignedMs = (ms: number) => `${ms < 0 ? "−" : "+"}${Math.abs(ms)}`;

/**
 * A mean delay and its spread, or an em dash when no click contributed a sample —
 * a zero mean is a real reading (screenshot and click on the same millisecond),
 * so it cannot double as "nothing was measured".
 *
 * Unsigned, because the pipeline measures each sample as a distance from the
 * click instant rather than as an offset from it: the concurrent window opens
 * before the click and closes after it, so a signed mean would cancel early
 * frames against late ones and report a tightness the samples never had.
 */
const fmtDelay = (meanMs: number | null | undefined, stddevMs: number | null | undefined) =>
  meanMs === null || meanMs === undefined || stddevMs === null || stddevMs === undefined
    ? "—"
    : `${Math.round(meanMs)} ± ${stddevMs.toFixed(0)} ms`;

/**
 * The "open the list behind this figure" control: the orphans of a level, the
 * invented names of a row, the errors behind a retry.
 *
 * Drawn as a button rather than as underlined text. These sit among a lot of
 * quiet grey figures, where an underlined word reads as a footnote — something
 * that has already told you what it has to say — rather than as a thing to
 * press. A border and a ground say it is a control at rest, without waiting for
 * a hover to admit it.
 */
const OpenListButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    // Stops the click from reaching a `<summary>` or a row handler above it: the
    // point of the press is to open the panel, never to collapse what it is in.
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-outline-strong bg-surface px-2 py-0.5 text-xs font-semibold text-info-strong shadow-sm transition-colors hover:border-info/50 hover:bg-info/10 focus:outline-none focus:ring-1 focus:ring-info/40"
  >
    {label}
    <ChevronDown className="h-3 w-3" />
  </button>
);

/**
 * The hover panel every mark in this report opens — the same Radix tooltip the
 * sibling analytics tabs use, portalled out of the flow so it is never clipped
 * by the `overflow-hidden` section or `overflow-x-auto` table box its mark sits
 * in. `place="below"` (default) hangs it under the mark's left edge;
 * `place="above"` centres it over the mark instead, for a mark that is a drawn
 * object rather than a glyph: a chart bar names itself above where the cursor
 * is, not below, where the panel would cover the bars the reader is comparing
 * it against.
 */
const TIP_MARGIN = 8;
const TIP_GAP = 6;

const Tip = ({
  panel,
  width,
  place = "below",
  className = "relative inline-flex align-middle",
  style,
  children,
}: {
  /** What the panel says. */
  panel: React.ReactNode;
  width: number;
  place?: "below" | "above";
  /** The wrapper's own classes: it stands in for the mark's box, so a mark that is not an inline glyph passes its own. */
  className?: string;
  /** The wrapper's own geometry, for a mark sized at runtime — a chart bar's height. */
  style?: React.CSSProperties;
  /** The mark itself: whatever the reader hovers to open the panel. Absent when the wrapper is already the mark — a bar is its own hover target. */
  children?: React.ReactNode;
}) => (
  <Tooltip.Provider delayDuration={0} skipDelayDuration={0}>
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className={className} style={style}>
          {children}
        </span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={place === "above" ? "top" : "bottom"}
          align={place === "above" ? "center" : "start"}
          sideOffset={TIP_GAP}
          collisionPadding={TIP_MARGIN}
          style={{ width }}
          className="pointer-events-none z-[100] flex flex-col gap-1.5 whitespace-normal rounded-md border border-outline bg-surface px-2 py-1.5 text-[11px] font-normal normal-case leading-snug tracking-normal text-text shadow-lg"
        >
          {panel}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  </Tooltip.Provider>
);

/**
 * The warning marker beside a flagged figure: hover the triangle, read why it is
 * flagged. Nothing at all when the figure is within bounds, so an unmarked stat
 * stays exactly as wide as it was.
 *
 * Some stats are flagged without a reason attached — the figure beside the
 * triangle is the whole reason — and those draw the marker with no panel at all
 * rather than a panel saying nothing.
 *
 * A stat can trip more than one rule (a stage that is both slow and dominant), in
 * which case the panel lists each reason — one marker per stat, however many
 * things are wrong with it.
 *
 * The panel is a `Tip`, so it is never clipped by the section or table the
 * marker sits in; see there.
 */
const WarnTip = ({ warnings }: { warnings: Warning[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (warnings.length === 0) return null;
  // A warning without a key explains nothing on purpose, so a stat carrying only
  // those draws the marker bare — flagged, with nothing to hover.
  const messages = warnings.flatMap(w => (w.key ? [t(w.key, w.params ?? {})] : []));
  const marker = (
    <button
      type="button"
      aria-label={messages.length > 0 ? messages.join(" ") : "warning"}
      // Hover-only, but it still swallows the click: in a section title it sits
      // inside a `<summary>`, where a click through to the parent would collapse
      // the very section the reader is asking about.
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      // The help cursor is a promise that hovering explains something, so a
      // marker with no panel behind it does not make one.
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-warning-strong transition-colors hover:text-danger focus:outline-none focus:ring-1 focus:ring-warning/40 ${
        messages.length > 0 ? "cursor-help" : "cursor-default"
      }`}
    >
      {/* Drawn heavier than the icon's default weight: at this size a 2px
            stroke reads as a grey smudge beside the figure it is flagging, and a
            marker that has to be looked for is not a marker. The size is held
            under the line-height of the rows it sits in, so weight carries most
            of the emphasis and no row grows to fit its marker. */}
      <TriangleAlert className="h-[17px] w-[17px]" strokeWidth={3} />
    </button>
  );
  return messages.length > 0 ? (
    <Tip width={256} panel={messages.map((message, i) => <span key={i}>{message}</span>)}>
      {marker}
    </Tip>
  ) : (
    marker
  );
};

/**
 * How many warnings a section holds, beside its title rather than out at the far
 * edge of the bar: the count is about that section, and at the width this report
 * runs to a right-aligned badge lands an inch of empty space away from the name
 * it belongs to. So a reader scanning the
 * collapsed report can see which sections are worth opening without opening any
 * of them. Absent rather than a zero when the section is clean: the point of the
 * badge is that it is rare.
 */
/**
 * Marks a figure this run did not produce: the stage behind it was copied whole
 * from the run this one resumed from, so the number describes that run's work.
 *
 * Deliberately not a warning — nothing is wrong with an inherited row, and
 * colouring it as a problem would be telling the reader to fix something that is
 * working as designed. It is a provenance label, so it takes the quiet
 * neutral-chip treatment the name chips do, and says the rest on hover.
 *
 * One chip marks one row or one section header; the report never marks a whole
 * table, because a stitched run's tables are exactly the ones with both kinds of
 * row in them.
 */
const InheritedChip = () => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    <Tip width={248} panel={t("inherited_hint")}>
      {/* Swallows the click for the same reason the hint mark does: a chip in a
          section title sits inside a `<summary>`, where the click would collapse
          the section the reader is inspecting. */}
      <span
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        className="shrink-0 cursor-help rounded-full border border-outline bg-muted-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-mid"
      >
        {t("inherited")}
      </span>
    </Tip>
  );
};

const WarningBadge = ({ warnings }: { warnings: Warning[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (warnings.length === 0) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-warning-strong/40 bg-warning/15 px-2 py-0.5 text-[11px] font-semibold normal-case tracking-normal text-warning-strong">
      <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.75} />
      {t("warnings_badge", { count: warnings.length })}
    </span>
  );
};

/**
 * One report section, collapsed until asked for. The whole report is admin
 * diagnostics — far too much to read at once — so it opens as a list of titles.
 *
 * A nested section is one of these inside another's body: the same box, drawn a
 * step quieter — thinner border, lighter shadow, smaller title — so that opening
 * the outer one reads as a list of sections within a section rather than as the
 * top-level list starting over one indent to the right.
 */
const Section = ({
  title,
  nested = false,
  holdsSections = false,
  hint,
  warnings = [],
  inherited = false,
  children,
}: {
  title: string;
  nested?: boolean;
  holdsSections?: boolean;
  hint?: string;
  /** Every warning inside, counted in the title bar so a collapsed section still says it has them. */
  warnings?: Warning[];
  /**
   * Whether the whole section reports on an inherited stage. Only the sections
   * that *are* one stage carry this (cleanup, annotation); a section whose rows
   * are per-stage marks the rows instead, since a stitched run's are mixed.
   */
  inherited?: boolean;
  children: React.ReactNode;
}) => (
  // The gap between sections is deliberately wider than the padding inside one:
  // proximity is what makes a box read as one thing, so a section has to sit
  // further from its neighbours than its own contents sit from each other. A
  // nested one's first sibling sits flush with the body's own padding instead.
  <details
    className={`group/section overflow-hidden rounded-xl bg-surface ${
      nested
        ? "mt-3 border border-outline-strong shadow-sm first:mt-0"
        : "mt-5 border-2 border-outline-strong shadow-md"
    }`}
  >
    {/* The title bar is the boundary marker — the one element that says a new
        section starts here — so it is the loudest thing in the section rather
        than the quietest. */}
    <summary
      className={`flex cursor-pointer list-none items-center gap-2 border-b border-outline-strong bg-surface px-3 [&::-webkit-details-marker]:hidden ${
        nested ? "py-2" : "py-3"
      }`}
    >
      <ChevronRight
        className={`shrink-0 text-text-mid transition-transform duration-200 group-open/section:rotate-90 ${
          nested ? "h-3.5 w-3.5" : "h-4 w-4"
        }`}
      />
      {/* The name takes a fixed column so that the badges beside it line up down
          the report rather than each starting wherever its own title happens to
          end — a ragged column of badges reads as noise scattered over the page
          instead of as one list of counts. Titles longer than the column simply
          push their badge along; none of the report's are.

          A nested title's column is the same one, less the 11px its own bar is
          indented by: the outer body's padding and border (14px) minus the
          lighter border and smaller chevron it draws itself (3px). That lands a
          nested badge on the same x as its parent's, so the badges read as one
          column down the whole report rather than one per nesting depth. */}
      <h3
        className={`flex shrink-0 items-center gap-1.5 font-bold uppercase tracking-wider text-text ${
          nested ? "min-w-[213px] text-[12px]" : "min-w-[224px] text-[13px]"
        }`}
      >
        {title}
        {hint && <HintTip text={hint} />}
      </h3>
      <WarningBadge warnings={warnings} />
      {/* After the badge, so the badge column stays where the fixed title column
          puts it: a chip inserted ahead of it would shift every badge on a
          stitched run's page and lose the alignment that column exists for. */}
      {inherited && <InheritedChip />}
    </summary>
    {/* The body is recessed below the title bar: everything inside it — the stat
        cards, the tables — is a white surface, and needs a ground to read as an
        object sitting on rather than dissolving into. A body holding sections
        rather than cards takes the page's own ground instead, so that a nested
        section sits on exactly what a top-level one sits on. */}
    <div className={`px-3 py-3 ${holdsSections ? "bg-background" : "bg-surface-sunken"}`}>
      {children}
    </div>
  </details>
);

/** One small labelled figure in a compact KPI card. */
const Stat = ({
  label,
  value,
  title,
  hint,
  warn = [],
}: {
  label: string;
  /** A node rather than a string only so provenance can put a link in one. */
  value: React.ReactNode;
  title?: string;
  hint?: string;
  warn?: Warning[];
}) => (
  <div className="rounded-xl border border-outline-strong bg-surface px-3 py-2.5 shadow-sm">
    <div className="flex items-center gap-1 text-[11px] uppercase tracking-wider text-text-dim font-medium">
      {label}
      {hint && <HintTip text={hint} />}
    </div>
    {/* The marker sits on the figure rather than the label: it is the reading
        that is out of bounds, not the thing being measured. */}
    <div className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-text tabular-nums">
      <span title={title}>{value}</span>
      <WarnTip warnings={warn} />
    </div>
  </div>
);

type DetailPopupContent = { title: string; text: string };

/**
 * The modal shell every popup in this report shares: the backdrop that closes
 * on click, the panel that swallows it, and the titled header with its close
 * button. The body — a text block, a table, a list — is the caller's.
 */
const Popup = ({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  // The one string this shell needs is the app-wide "close", not a report key.
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 px-6 py-8 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col rounded-3xl border border-outline-strong bg-surface p-6 text-text shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">{title}</h3>
          <button
            type="button"
            className="text-text-dim transition-colors hover:text-text"
            onClick={onClose}
            aria-label={t("close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/**
 * A modal for the long free-text evidence (retry errors, raw vision responses)
 * that would clutter its table row: the row shows a truncated preview, the
 * popup the whole thing.
 */
const DetailPopup = ({
  content,
  onClose,
}: {
  content: DetailPopupContent;
  onClose: () => void;
}) => (
  <Popup title={content.title} onClose={onClose}>
    <pre className="mt-3 overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-outline bg-muted-surface/30 p-3 text-xs leading-5 text-text">
      {content.text}
    </pre>
  </Popup>
);

/** A one-line preview of a detail text, with the popup trigger beside it. */
const DetailCell = ({
  preview,
  onOpen,
  openLabel,
}: {
  preview: string;
  onOpen: () => void;
  openLabel: string;
}) => (
  <span className="inline-flex max-w-full items-center gap-2">
    <span className="max-w-[24rem] truncate align-bottom text-text-mid">{preview}</span>
    <OpenListButton label={openLabel} onClick={onOpen} />
  </span>
);

/**
 * A column heading's "what is this figure" mark: hover the question mark, get the
 * definition.
 *
 * The panel is a `Tip`, so it is never clipped by the section or table the mark
 * sits in; see there.
 */
const HintTip = ({ text }: { text: string }) => (
  <Tip width={224} panel={text}>
    {/* Reachable by keyboard, and normal case: the heading around it is
        uppercased, which would letterspace a lone "?" away from its circle.
        `border-current` keeps the ring the weight and colour of the glyph inside
        it, so the mark reads as one object rather than as a "?" in a box. */}
    <button
      type="button"
      aria-label={text}
      // The mark is hover-only, but it still swallows the click: in a section
      // title it sits inside a `<summary>`, where any click through to the parent
      // would collapse the very section the reader is asking about.
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex h-4 w-4 cursor-help items-center justify-center rounded-full border-[1.5px] border-current text-[10px] font-bold normal-case leading-none text-text-mid transition-colors hover:text-text focus:outline-none focus:ring-1 focus:ring-info/40"
    >
      ?
    </button>
  </Tip>
);

type Align = "left" | "center";

const TH = ({
  children,
  align = "left",
  hint,
}: {
  children: React.ReactNode;
  align?: Align;
  hint?: string;
}) => (
  <th
    className={`px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-dim ${
      align === "center" ? "text-center" : "text-left"
    }`}
  >
    <span
      className={`inline-flex items-center gap-1 ${align === "center" ? "justify-center" : ""}`}
    >
      {children}
      {hint && <HintTip text={hint} />}
    </span>
  </th>
);

const TD = ({
  children,
  align = "left",
  hint,
  warn = [],
}: {
  children: React.ReactNode;
  align?: Align;
  hint?: string;
  warn?: Warning[];
}) => (
  <td
    className={`px-2 py-1.5 text-sm text-text tabular-nums ${align === "center" ? "text-center" : ""}`}
  >
    {/* Only wrapped when there is a mark to sit beside: a plain cell's children
        can be block-level (the name chips), which an inline wrapper would be an
        invalid parent for. */}
    {hint || warn.length > 0 ? (
      <span className="inline-flex items-center gap-1">
        {children}
        <WarnTip warnings={warn} />
        {hint && <HintTip text={hint} />}
      </span>
    ) : (
      children
    )}
  </td>
);

/**
 * A table cell naming a stage or level, with the inherited chip beside it when
 * that stage's row was copied. Every table on the page marks its rows through
 * this, so the chip cannot end up in a different column in one of them.
 */
const RowLabel = ({
  label,
  inherited,
  hint,
}: {
  label: string;
  inherited: boolean;
  hint?: string;
}) => (
  <TD hint={hint}>
    <span className="inline-flex items-center gap-1.5">
      {label}
      {inherited && <InheritedChip />}
    </span>
  </TD>
);

/**
 * The subsection heading style shared inside a report section. `first` drops
 * the top margin, for a heading that opens its box.
 */
const SubHeading = ({
  first = false,
  hint,
  children,
}: {
  first?: boolean;
  hint?: string;
  children: React.ReactNode;
}) => (
  <p
    className={`${first ? "" : "mt-4 "}mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-text-dim`}
  >
    {children}
    {hint && <HintTip text={hint} />}
  </p>
);

/** `version:branch:commit`, as `build.rs` stamps `CLANK_SENTRY_RELEASE`. */
const parseRelease = (release: string) => {
  const [version, branch, commit] = release.split(":");
  return version && branch && commit ? { version, branch, commit } : null;
};

/**
 * A build as `version · short-commit`: the version tells the deployments apart
 * (staging builds carry rc/nightly modifiers), the commit pins the exact code.
 * A build made on a branch — rather than the detached checkout deployments are
 * built from, whose branch reads `HEAD` — is marked `dev`: its version is the
 * unbumped one of whatever release it forked from, not a release of its own.
 * Branch and full commit are on hover.
 */
const formatBuild = (release: string): string => {
  const parsed = parseRelease(release);
  if (!parsed) return release;
  const version = parsed.branch === "HEAD" ? parsed.version : `${parsed.version} dev`;
  return `${version} · ${parsed.commit.slice(0, 7)}`;
};

const buildTitle = (release: string): string | undefined => {
  const parsed = parseRelease(release);
  return parsed ? `${parsed.branch} @ ${parsed.commit}` : undefined;
};

/**
 * The distinct builds behind the report, in pipeline order, first occurrence
 * winning. Stages are re-runnable one by one, so a report can carry several.
 * Stages that recorded no build at all drop out.
 */
/**
 * A stage under the name the re-run dialog offers it as ("Lift L3
 * (Process-steps)"), not the table's terse "L3": provenance is read once, on its
 * own, by someone reconstructing what happened — and the same words they picked
 * the stage with are the ones that confirm they picked it.
 */
const stageDisplay = (stage: AnalyzerStage): string =>
  ANALYZER_STAGES.find(known => known.id === stage)?.display ?? stage;

const distinctBuilds = (provenance: StageProvenance[]): string[] => {
  const seen = new Set<string>();
  for (const { release } of provenance) {
    if (release) seen.add(release);
  }
  return [...seen];
};

/**
 * Which build produced this analysis.
 *
 * Almost always one whole run under one build, so the build reads as a single
 * figure. The rarer mixed run — some stage re-run under newer code — lists
 * every build that contributed rather than claiming one that did not produce
 * the whole report.
 */
const ProvenanceSection = ({
  recordingId,
  runId,
  provenance,
  resumedFrom,
  knownRunIds,
}: {
  recordingId: string;
  /** The run these diagnostics describe, as the server resolved it. */
  runId: string;
  provenance: StageProvenance[];
  /** The run this one resumed from, or `null` for a full run. */
  resumedFrom: ResumedFrom | null;
  /**
   * The runs this recording still has, so a base run that has since been deleted
   * is told apart from one that can be opened. Copying is what lets a run outlive
   * its ancestors, so a dangling `baseRunId` is a normal state, not an error.
   */
  knownRunIds: string[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const navigate = useNavigate();
  const location = useLocation();
  const builds = distinctBuilds(provenance);

  const build = builds.length === 0 ? t("not_recorded") : builds.map(formatBuild).join(", ");
  const title = builds.length === 1 ? buildTitle(builds[0]) : undefined;

  // The run picker already addresses a run as `?run=<id>` on this very page, so
  // the base run's link is this URL with that one parameter swapped — no route
  // to construct, and it lands on the run with the tab the reader is on kept.
  const baseRunHref = (runId: string) => {
    const params = new URLSearchParams(location.search);
    params.set("run", runId);
    return `${location.pathname}?${params.toString()}${location.hash}`;
  };

  return (
    <Section title={t("provenance")}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Stat label={t("recording_id")} value={recordingId} />
        <Stat label={t("build")} value={build} title={title} />
        {/* Which run this is, said outright: the picker names runs by the minute
            they ran, and a resumed run's own box below names another id — two
            reasons the reader cannot infer this one from the page. */}
        <Stat label={t("run_id")} value={runId} />
        {resumedFrom && (
          // Lineage as one sentence rather than a run box beside a stage box:
          // neither half means anything alone, and split across two cards the
          // reader has to put them back together to read "this run started at
          // L4 with that run's L3".
          <Stat
            label={t("resumed_from_stage_of_run", {
              stage: stageDisplay(resumedFrom.startStage),
            })}
            value={
              knownRunIds.includes(resumedFrom.baseRunId) ? (
                // A real href, so the link can be copied and opened in a new
                // tab like any other; the click is intercepted to navigate in
                // place, which keeps the page's state instead of reloading it.
                <a
                  href={baseRunHref(resumedFrom.baseRunId)}
                  onClick={event => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
                      return;
                    }
                    event.preventDefault();
                    navigate(baseRunHref(resumedFrom.baseRunId), {
                      state: location.state,
                    });
                  }}
                  className="text-info underline decoration-dotted underline-offset-2 hover:decoration-solid"
                >
                  {resumedFrom.baseRunId}
                </a>
              ) : (
                // Deleted, so there is nothing to open. The id still shows: it
                // is the only record of where these artifacts came from, and
                // dropping it would leave the run looking like a full one.
                <span className="text-text-mid">
                  {resumedFrom.baseRunId} ({t("resumed_from_run_deleted")})
                </span>
              )
            }
          />
        )}
      </div>
    </Section>
  );
};

const NameChips = ({ names }: { names: string[] }) => (
  <div className="mt-1 flex flex-wrap gap-1">
    {names.map(name => (
      <span
        key={name}
        className="rounded-full border border-outline bg-muted-surface/50 px-2 py-0.5 text-xs text-text-mid"
      >
        {name}
      </span>
    ))}
  </div>
);

/**
 * One level's vocabulary row: how much of its naming the catalogue covered, and
 * how hard the model leaned on each name it had to invent.
 */
const VocabLevelRow = ({
  level,
  inherited,
  fromVocab,
  invented,
  inventedNames,
  onShowNames,
  warnings,
}: {
  level: string;
  /** Whether the lift that named these nodes was copied from the base run. */
  inherited: boolean;
  fromVocab: number;
  invented: number;
  inventedNames: InventedName[];
  onShowNames: () => void;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const named = fromVocab + invented;
  const reuse = nameReuse(invented, inventedNames.length);
  // No named node at all at this level: nothing was covered, invented, or
  // re-used, so every figure is an em dash rather than a 0% / 0 that would read
  // as a level that named things badly.
  if (named === 0) {
    return (
      <tr className="border-b border-outline last:border-b-0">
        <RowLabel label={level} inherited={inherited} />
        <TD>{"—"}</TD>
        <TD>{"—"}</TD>
        <TD>{"—"}</TD>
      </tr>
    );
  }
  return (
    <tr className="border-b border-outline last:border-b-0">
      <RowLabel label={level} inherited={inherited} />
      <TD>{`${fromVocab}/${named} (${sharePct(fromVocab, named)})`}</TD>
      <TD>
        <span className="inline-flex items-center gap-2">
          <span title={`${invented} ${t("invented").toLowerCase()}`}>{inventedNames.length}</span>
          {/* The list itself is a click away: inline it would bury the table it
              belongs to under a wall of chips. */}
          {inventedNames.length > 0 && (
            <OpenListButton label={t("show_invented_names")} onClick={onShowNames} />
          )}
        </span>
      </TD>
      {/* Re-use, not occurrences: the node that introduces a name has not
          re-used it, so a name used exactly once scores 0 rather than 1×. */}
      <TD warn={warningsFor(warnings, warnId.avgReuse(level))}>
        {reuse !== null ? `${fmtAvg(reuse)}×` : "—"}
      </TD>
    </tr>
  );
};

/**
 * One level's invented names with how hard each was leaned on. Re-uses, not
 * occurrences, to match the `avg_reuse` column: the node that introduces a name
 * has not re-used it, so a name on exactly one node scores 0.
 */
const InventedNamesPopup = ({
  title,
  names,
  onClose,
}: {
  title: string;
  names: InventedName[];
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // The "Name" column heading is the app-wide key, not a report one.
  const { t: tApp } = useTranslation();
  return (
    <Popup title={title} onClose={onClose}>
      <div className="mt-3 overflow-y-auto rounded-xl border border-outline">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{tApp("name")}</TH>
              <TH align="center">{t("nodes")}</TH>
              <TH align="center" hint={t("name_reuses_hint")}>
                {t("name_reuses")}
              </TH>
            </tr>
          </thead>
          <tbody>
            {names.map(({ name, uses }) => (
              <tr key={name} className="border-b border-outline last:border-b-0">
                <TD>{name}</TD>
                <TD align="center">{uses}</TD>
                <TD align="center">{uses - 1}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Popup>
  );
};

/** How much of the L3/L4 naming reused the vocabulary vs was invented. */
const VocabSection = ({
  vocab,
  isLevelInherited,
  warnings,
}: {
  vocab: VocabReport;
  /**
   * Whether a level's nodes came from the base run. The coverage is recomputed
   * from the stored analysis either way — what the chip marks is the lift that
   * chose these names, which on an inherited level was not this run's.
   */
  isLevelInherited: (depth: number) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const [shown, setShown] = useState<{
    title: string;
    names: InventedName[];
  } | null>(null);
  return (
    <Section title={t("vocabulary_usage")} warnings={warnings}>
      <div className="overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{t("level")}</TH>
              <TH>{t("vocab_coverage")}</TH>
              <TH>{t("invented_names")}</TH>
              <TH hint={t("avg_reuse_hint")}>{t("avg_reuse")}</TH>
            </tr>
          </thead>
          <tbody>
            <VocabLevelRow
              level="L3"
              inherited={isLevelInherited(3)}
              fromVocab={vocab.stepNodesFromVocab}
              invented={vocab.stepNodesInvented}
              inventedNames={vocab.inventedStepNames}
              onShowNames={() =>
                setShown({
                  title: `${t("invented_step_names")} · L3`,
                  names: vocab.inventedStepNames,
                })
              }
              warnings={warnings}
            />
            <VocabLevelRow
              level="L4"
              inherited={isLevelInherited(4)}
              fromVocab={vocab.processNodesFromVocab}
              invented={vocab.processNodesInvented}
              inventedNames={vocab.inventedProcessNames}
              onShowNames={() =>
                setShown({
                  title: `${t("invented_process_names")} · L4`,
                  names: vocab.inventedProcessNames,
                })
              }
              warnings={warnings}
            />
          </tbody>
        </table>
      </div>
      {shown && (
        <InventedNamesPopup
          title={shown.title}
          names={shown.names}
          onClose={() => setShown(null)}
        />
      )}
    </Section>
  );
};

/** A section whose data this run did not record, kept visible so its absence reads. */
const EmptySection = ({
  title,
  nested = false,
  inherited = false,
}: {
  title: string;
  nested?: boolean;
  inherited?: boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    <Section title={title} nested={nested} inherited={inherited}>
      <p className="text-sm text-text-mid">{t("section_not_recorded")}</p>
    </Section>
  );
};

/**
 * Every cleanup pass's row label, keyed by the pass the backend names — so a pass
 * added there does not compile here until it is given one.
 */
const CLEANUP_PASS_LABEL: Record<CleanupPass, AnalysisReportKey> = {
  "drop-metadata": "cleanup_pass_drop_metadata",
  "insert-meeting-video": "cleanup_pass_insert_meeting_video",
  "merge-same-type": "cleanup_pass_merge_same_type",
  "drop-clicks-without-screenshot": "cleanup_pass_drop_clicks_without_screenshot",
  "drop-short-tab-visits": "cleanup_pass_drop_short_tab_visits",
  "merge-typing": "cleanup_pass_merge_typing",
};

/**
 * The passes whose rule is worth spelling out, keyed the same way. Partial by
 * design: a pass whose label already says what it does gets no mark rather than
 * a hint restating the label.
 */
const CLEANUP_PASS_HINT: Partial<Record<CleanupPass, AnalysisReportKey>> = {
  "insert-meeting-video": "cleanup_pass_insert_meeting_video_hint",
  "merge-same-type": "cleanup_pass_merge_same_type_hint",
  "drop-short-tab-visits": "cleanup_pass_drop_short_tab_visits_hint",
};

const fmtCount = (value: number) => value.toLocaleString();

/**
 * An event type as a column heading. The backend names them in snake_case; the
 * heading is already uppercased by `TH`, so the underscores only read as noise.
 */
const fmtEventType = (eventType: string) => eventType.replaceAll("_", " ");

/**
 * A share of the raw stream, to one decimal. The metadata pass alone leaves
 * single-digit percentages, which whole numbers would round into each other.
 */
const fmtShare = (part: number, whole: number) => (whole > 0 ? ` (${sharePct(part, whole)})` : "");

/** A pass's signed change; typographic minus, to sit at the width of the plus. */
const fmtDelta = (delta: number) =>
  `${delta > 0 ? "+" : delta < 0 ? "−" : ""}${fmtCount(Math.abs(delta))}`;

/**
 * What cleanup made of the raw recording: the funnel from raw events down to
 * cleaned ones, then the type distribution of what came out.
 *
 * A table rather than a row of figures because the cumulative share is the point:
 * the metadata pass alone drops the large majority, and the passes after it have
 * to be read against what it left rather than against the recording.
 */
const CleanupSection = ({
  cleanup,
  inherited,
  warnings,
}: {
  cleanup: CleanupReport | null;
  /** Whether this run copied the cleanup stage rather than running it. */
  inherited: boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (!cleanup) return <EmptySection title={t("cleanup_stats")} nested inherited={inherited} />;

  const raw = cleanup.rawEventCount;
  const cleaned = cleanedEventCount(cleanup);

  // Every threshold-driven hint quotes the value *this run* applied, from the
  // report, rather than a constant restated here. A pass retuned in the pipeline
  // would otherwise leave these descriptions quietly describing the old
  // behaviour — which is the reason the backend reports them at all.
  //
  // The tab bound is printed as its own seconds rather than through
  // `formatDuration`, which rounds to whole seconds and would show the 1.5s
  // bound as "2s" — a wrong figure in a sentence whose only job is that figure.
  const hintValues = {
    idle: formatDuration(cleanup.thresholds.idleThresholdS),
    tab: `${cleanup.thresholds.tabMinDurationS}s`,
  };

  return (
    <Section title={t("cleanup_stats")} nested inherited={inherited} warnings={warnings}>
      <div className="flex flex-wrap items-start gap-6">
        {/* Three narrow columns: capped so the pass name and its figures stay
          readable together instead of sitting at opposite edges of the tab. */}
        <div className="min-w-0 flex-1 basis-[28rem] overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
          <table className="w-full border-collapse">
            <thead className="border-b border-outline-strong bg-muted-surface">
              <tr>
                <TH>{t("cleanup_pass")}</TH>
                <TH hint={t("cleanup_delta_hint")}>{t("cleanup_delta")}</TH>
                <TH align="center">{t("cleanup_remaining_events")}</TH>
              </tr>
            </thead>
            <tbody>
              {/* The funnel's two ends — what came in and what came out — carry the
                weight; the passes between them are the working. */}
              <tr className="border-b border-outline font-semibold">
                <TD hint={t("cleanup_pass_raw_events_hint")}>{t("cleanup_pass_raw_events")}</TD>
                <TD>{""}</TD>
                <TD align="center">{`${fmtCount(raw)}${fmtShare(raw, raw)}`}</TD>
              </tr>
              {cleanup.passes.map((row, i) => {
                const delta = row.eventsAfter - passInputCount(cleanup, i);
                const hintKey = CLEANUP_PASS_HINT[row.pass];
                return (
                  <tr key={row.pass} className="border-b border-outline last:border-b-0">
                    <TD hint={hintKey && t(hintKey, hintValues)}>
                      {t(CLEANUP_PASS_LABEL[row.pass])}
                    </TD>
                    {/* The insert pass is the only one that grows the stream —
                      weighted so its Δ does not read as another drop. */}
                    <TD warn={warningsFor(warnings, warnId.cleanupPass(row.pass))}>
                      <span className={delta > 0 ? "font-semibold" : undefined}>
                        {fmtDelta(delta)}
                      </span>
                    </TD>
                    <TD align="center">
                      {`→ ${fmtCount(row.eventsAfter)}${fmtShare(row.eventsAfter, raw)}`}
                    </TD>
                  </tr>
                );
              })}
              <tr className="border-t border-outline font-semibold">
                <TD>{t("cleanup_cleaned_events")}</TD>
                <TD>{""}</TD>
                <TD align="center" warn={warningsFor(warnings, warnId.cleanedEvents())}>
                  {`${fmtCount(cleaned)}${fmtShare(cleaned, raw)}`}
                </TD>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="min-w-0 flex-1 basis-[28rem]">
          <SubHeading first>{t("cleanup_event_types")}</SubHeading>
          {/* One bar per type, scaled against the cleaned stream, so a full-width bar
          reads as 100% of what came out of cleanup. */}
          <div className="rounded-xl border border-outline-strong bg-surface p-4 shadow-sm">
            {[...cleanup.eventTypes]
              .sort((a, b) => b.count - a.count)
              .map(entry => {
                const width = cleaned > 0 ? (entry.count / cleaned) * 100 : 0;
                return (
                  <div key={entry.eventType} className="flex items-center gap-3 py-1.5 text-xs">
                    <span
                      className="w-28 shrink-0 truncate text-text-dim"
                      title={fmtEventType(entry.eventType)}
                    >
                      {fmtEventType(entry.eventType)}
                    </span>
                    <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted-surface">
                      <div
                        className="h-full rounded-full bg-accent"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="w-24 shrink-0 text-right tabular-nums">
                      {`${fmtCount(entry.count)}${fmtShare(entry.count, cleaned)}`}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </Section>
  );
};

/**
 * One row of the annotation funnel. `depth` indents the label to show what
 * branches off what; `bold` weights the rows the funnel is read for — what came
 * in, what was billed for, and what came out — over the working between them.
 *
 * A breakdown row that counts nothing on this run says nothing, so it is left
 * out; the bold rows stay whatever they read, since a funnel missing its own
 * endpoints reads as a broken table rather than a clean run.
 */
const FunnelRow = ({
  label,
  count,
  total,
  depth = 0,
  bold = false,
  title,
  hint,
  warn,
}: {
  label: string;
  count: number;
  total: number;
  depth?: number;
  bold?: boolean;
  title?: string;
  hint?: string;
  warn?: Warning[];
}) =>
  count === 0 && !bold ? null : (
    <tr className={`border-b border-outline last:border-b-0 ${bold ? "font-semibold" : ""}`}>
      <TD>
        <span
          className="inline-flex items-center gap-1"
          style={{ paddingLeft: `${depth * 0.875}rem` }}
          title={title}
        >
          {label}
          {hint && <HintTip text={hint} />}
        </span>
      </TD>
      <TD warn={warn}>{`${fmtCount(count)}${fmtShare(count, total)}`}</TD>
    </tr>
  );

/**
 * Every failure kind's row label, keyed by the kind the backend names — so a
 * kind added there does not compile here until it is given one.
 */
const VISION_FAILURE_KIND_LABEL: Record<VisionFailureKind, AnalysisReportKey> = {
  "api-error": "vision_failure_kind_api_error",
  truncated: "vision_failure_kind_truncated",
  "no-json": "vision_failure_kind_no_json",
  "malformed-json": "vision_failure_kind_malformed_json",
  "unknown-target": "vision_failure_kind_unknown_target",
};

/**
 * What the annotator made of the recording's clicks.
 *
 * The funnel is the section: every click candidate either dies before the model
 * (no usable screenshot — costs nothing, our data's fault) or is sent and either
 * fails (billed, no label — the model's fault) or lands. Splitting those two is
 * the whole point of the table, since only one of them shows up in the costs.
 */
const AnnotationSection = ({
  annotation,
  thresholds,
  recordingHours,
  inherited,
  warnings,
}: {
  annotation: AnnotationReport | null;
  thresholds: CleanupThresholds | null;
  recordingHours: number;
  /** Whether this run copied the annotator stage rather than running it. */
  inherited: boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (!annotation)
    return <EmptySection title={t("annotation_stats")} nested inherited={inherited} />;

  const total = annotation.clicksTotal;

  // The pairing windows are cleanup's to report, and a run that recorded no
  // cleanup section reports no window either — so the sentence describing it is
  // appended only when there is one, rather than stating a constant this page
  // would then own. The post-click bound is younger than the block it sits in,
  // so it is missing on its own for a report from between the two.
  const delayHint = (
    base: AnalysisReportKey,
    windowKey: AnalysisReportKey,
    window: string | null,
  ) => (window === null ? t(base) : `${t(base)} ${t(windowKey, { window })}`);
  const concurrentWindow = thresholds
    ? `${fmtSignedMs(thresholds.clickScreenshotWindowMinMs)} to ${fmtSignedMs(
        thresholds.clickScreenshotWindowMaxMs,
      )} ms`
    : null;
  const postWindow = thresholds?.postScreenshotWindowMaxMs
    ? formatDuration(thresholds.postScreenshotWindowMaxMs / 1000)
    : null;

  return (
    <Section title={t("annotation_stats")} nested inherited={inherited} warnings={warnings}>
      {/* The funnel is only two narrow columns, so it is capped and the figures
          it does not carry sit beside it rather than under a table stretched to
          the width of the tab. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        <div className="w-full shrink-0 overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm lg:max-w-md">
          <table className="w-full border-collapse">
            <thead className="border-b border-outline-strong bg-muted-surface">
              <tr>
                <TH>{t("annotation_funnel_outcome")}</TH>
                <TH>{t("annotation_funnel_clicks")}</TH>
              </tr>
            </thead>
            <tbody>
              <FunnelRow
                label={t("annotation_funnel_candidates")}
                count={total}
                total={total}
                bold
                hint={t("annotation_funnel_candidates_hint")}
              />
              <FunnelRow
                label={t("annotation_funnel_dropped")}
                count={annotation.clicksDropped}
                total={total}
                depth={1}
              />
              <FunnelRow
                label={t("drop_reason_concurrent_missing")}
                count={annotation.droppedConcurrentMissing}
                total={total}
                depth={2}
              />
              <FunnelRow
                label={t("drop_reason_other")}
                count={annotation.droppedOther}
                total={total}
                depth={2}
              />
              <FunnelRow
                label={t("annotation_funnel_sent")}
                count={annotation.clicksSent}
                total={total}
                bold
              />
              <FunnelRow
                label={t("sent_without_post_window")}
                count={annotation.sentNoPostWindow}
                total={total}
                depth={1}
              />
              <FunnelRow
                label={t("sent_post_missing")}
                count={annotation.sentPostMissing}
                total={total}
                depth={1}
              />
              <FunnelRow
                label={t("annotation_funnel_failed")}
                count={annotation.visionFailed}
                total={total}
                depth={1}
                warn={warningsFor(warnings, warnId.visionTruncated())}
              />
              <FunnelRow
                label={t("annotation_funnel_annotated")}
                count={annotation.clicksAnnotated}
                total={total}
                bold
              />
            </tbody>
          </table>
        </div>
        {/* Paired across each row: the count, then the rate it came at. */}
        <div className="grid flex-1 grid-cols-2 content-start gap-3">
          <Stat
            label={t("annotated_clicks")}
            value={`${fmtCount(annotation.clicksAnnotated)}/${fmtCount(total)}${fmtShare(
              annotation.clicksAnnotated,
              total,
            )}`}
            warn={warningsFor(warnings, warnId.annotatedClicks())}
          />
          <Stat
            label={t("annotated_clicks_per_hour")}
            value={fmtRate(perHour(annotation.clicksAnnotated, recordingHours))}
            warn={warningsFor(warnings, warnId.annotatedPerHour())}
          />
          <Stat
            label={t("screenshots_used_label")}
            value={`${fmtCount(annotation.screenshotsUsed)}/${fmtCount(annotation.screenshotsTotal)}${fmtShare(
              annotation.screenshotsUsed,
              annotation.screenshotsTotal,
            )}`}
            warn={warningsFor(warnings, warnId.screenshotsUsed())}
          />
          <Stat
            label={t("screenshots_used_per_hour")}
            value={fmtRate(perHour(annotation.screenshotsUsed, recordingHours))}
          />
          <Stat
            label={t("concurrent_screenshot_delay")}
            hint={delayHint(
              "concurrent_screenshot_delay_hint",
              "concurrent_screenshot_delay_window_hint",
              concurrentWindow,
            )}
            value={fmtDelay(annotation.concurrentDelayMeanMs, annotation.concurrentDelayStddevMs)}
            warn={warningsFor(warnings, warnId.concurrentDelay())}
          />
          <Stat
            label={t("post_screenshot_delay")}
            hint={delayHint(
              "post_screenshot_delay_hint",
              "post_screenshot_delay_window_hint",
              postWindow,
            )}
            value={fmtDelay(annotation.postDelayMeanMs, annotation.postDelayStddevMs)}
            warn={warningsFor(warnings, warnId.postDelay())}
          />
        </div>
      </div>
      {/* The failures behind the funnel's `failed` row are broken down in the
          repair-retries section, alongside the lifts' — both are a response the
          run could not use. */}
    </Section>
  );
};

/** The unit of work one logical call does, per stage. */
const LOGICAL_UNIT_KEY: Partial<Record<StageCostReport["stage"], "screenshots" | "chunks">> = {
  annotator: "screenshots",
  "lift-to-l1": "chunks",
  "lift-to-l2": "chunks",
  "lift-to-l3": "chunks",
  "lift-to-l4": "chunks",
};

/**
 * The tools a stage offered the model, each with the calls it received, sorted
 * most-used first so a table of them opens on what the model actually leaned on.
 * Empty means the stage offered none — distinct from offering tools nobody
 * called, which comes back as entries sitting at zero.
 */
const offeredTools = (stage: StageCostReport): [string, number][] =>
  Object.entries(stage.tools ?? {}).sort(([aName, a], [bName, b]) =>
    b - a !== 0 ? b - a : aName.localeCompare(bName),
  );

const fmtDurationMs = (ms: number | null | undefined, fallback: string) =>
  ms === null || ms === undefined ? fallback : formatDuration(ms / 1000);

/**
 * A stage's time as `duration (share)`. The share is dropped when there is no
 * total to divide by — an untimed stage among timed ones, or a whole report from
 * before the pipeline measured time — rather than shown as a 0%.
 */
const fmtDurationShare = (
  ms: number | null | undefined,
  totalMs: number | null,
  fallback: string,
) => {
  const duration = fmtDurationMs(ms, fallback);
  return ms != null && totalMs && totalMs > 0 ? `${duration} (${pct(ms / totalMs)})` : duration;
};

/** Compact token count: 10.4k, 950, 1.5k. */
const fmtTokens = (value: number) =>
  value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(Math.round(value));

/** A cost rated against the recording length, an em dash when it has none. */
const fmtCostPerHour = (costUsd: number, recordingHours: number) => {
  const rate = perHour(costUsd, recordingHours);
  return rate === null ? "—" : fmtCost(rate);
};

/**
 * What each stage cost and how long it took, with each stage's share of the
 * total. Cleanup and the Adapter have no row: they run no model, so they cost
 * nothing, and their time is negligible next to the LLM stages.
 *
 * The time column drops out entirely for a report from before the pipeline
 * measured time, rather than standing there as a column of "not recorded". A
 * single row can still say that on its own, though: a partial `--from` re-run
 * mixes freshly-timed stages with older untimed ones.
 *
 * A resumed run gets a **re-run** line above the total, summing only the stages
 * it executed. Without it the table's bottom line is the cost and time of
 * producing these artifacts across every run that contributed — a real figure,
 * and the one the budget is judged on, but not an answer to "what did this
 * re-run cost me", which is otherwise nowhere in the report. Both lines are
 * shown because they answer different questions; on a full run they are the same
 * number, so only the total is drawn.
 *
 * Every percentage stays against the total, including the re-run line's own: a
 * column whose rows divided by different denominators would no longer sum to
 * 100%, and the point of the line is precisely its share of the whole.
 */
const CostTimeTable = ({
  stages,
  totalCostUsd,
  totalDurationMs,
  recordingHours,
  isInherited,
  warnings,
}: {
  stages: StageCostReport[];
  totalCostUsd: number;
  totalDurationMs: number | null;
  recordingHours: number;
  isInherited: (stage: StageCostReport["stage"]) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const notRecorded = t("not_recorded");
  const hasTime = totalDurationMs !== null || stages.some(s => s.durationMs != null);
  const ownStages = stages.filter(stage => !isInherited(stage.stage));
  // Only worth a line when something was in fact inherited: on a full run it
  // would restate the total directly above it.
  const hasInherited = ownStages.length < stages.length;
  const rerunCostUsd = ownStages.reduce((sum, stage) => sum + stage.knownCostUsd, 0);
  // Summed like the total is — over the rows that carry a duration — so an
  // untimed row costs the line its contribution rather than the whole reading.
  // `null` when this run executed no timed stage at all, which is a resume at
  // the Adapter: it runs no model, so it has no row here and nothing to sum.
  const ownDurations = ownStages.flatMap(stage => stage.durationMs ?? []);
  const rerunDurationMs =
    ownDurations.length > 0 ? ownDurations.reduce((sum, ms) => sum + ms, 0) : null;
  // The rate is divided by the *recording's* hours, the same denominator the
  // total's rate uses, so the re-run's share of the rate is its share of the
  // cost — restated here rather than left for the reader to carry across two
  // columns. Dropped when there is no rate to qualify (no recording length) or
  // no priced row to take a share of.
  const rerunRate = fmtCostPerHour(rerunCostUsd, recordingHours);
  const rerunRateLabel =
    rerunRate === "—" || totalCostUsd <= 0
      ? rerunRate
      : `${rerunRate} (${pct(rerunCostUsd / totalCostUsd)})`;
  // Dropped wholesale for a report from before the alias was recorded, like the
  // time column: a column of "not recorded" says nothing a missing column doesn't.
  const hasModel = stages.some(s => s.model);
  return (
    <div className="overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
      <table className="w-full border-collapse">
        <thead className="border-b border-outline-strong bg-muted-surface">
          <tr>
            <TH>{t("stage")}</TH>
            {hasModel && <TH>{t("model")}</TH>}
            {hasTime && <TH hint={t("time_hint")}>{t("time")}</TH>}
            <TH>{t("cost")}</TH>
            <TH>{t("cost_per_hour")}</TH>
          </tr>
        </thead>
        <tbody>
          {stages.map(stage => (
            <tr key={stage.stage} className="border-b border-outline last:border-b-0">
              <RowLabel label={fmtStage(stage.stage)} inherited={isInherited(stage.stage)} />
              {hasModel && (
                <td className="px-2 py-1.5 text-sm text-text-mid">
                  {/* Not a `TD`: an alias is a name, not a figure, and the
                      tabular-nums treatment there is for columns of numbers. */}
                  {stage.model ?? notRecorded}
                </td>
              )}
              {hasTime && (
                <TD warn={warningsFor(warnings, warnId.stageTime(stage.stage))}>
                  {fmtDurationShare(stage.durationMs, totalDurationMs, notRecorded)}
                </TD>
              )}
              {/* A zero total would only happen with no priced rows at all, in
                  which case there is no share to show rather than a 0%. */}
              <TD>
                {totalCostUsd > 0
                  ? `${fmtCost(stage.knownCostUsd)} (${pct(stage.knownCostUsd / totalCostUsd)})`
                  : fmtCost(stage.knownCostUsd)}
              </TD>
              <TD>{fmtCostPerHour(stage.knownCostUsd, recordingHours)}</TD>
            </tr>
          ))}
          {hasInherited && (
            <tr className="border-t border-outline-strong bg-muted-surface/50">
              <TD>{t("rerun_total")}</TD>
              {hasModel && <td className="px-2 py-1.5" />}
              {hasTime && (
                <TD>{fmtDurationShare(rerunDurationMs, totalDurationMs, notRecorded)}</TD>
              )}
              <TD>
                {totalCostUsd > 0
                  ? `${fmtCost(rerunCostUsd)} (${pct(rerunCostUsd / totalCostUsd)})`
                  : fmtCost(rerunCostUsd)}
              </TD>
              <TD>{rerunRateLabel}</TD>
            </tr>
          )}
          <tr className="border-t border-outline-strong bg-muted-surface font-semibold">
            <TD>{t("total")}</TD>
            {/* A total has no model: the stages do not share one. */}
            {hasModel && <td className="px-2 py-1.5" />}
            {hasTime && <TD>{fmtDurationMs(totalDurationMs, notRecorded)}</TD>}
            <TD>{fmtCost(totalCostUsd)}</TD>
            {/* The budget is a fact about the run, not about any one stage, so
                the marker sits on the total rather than on each row that
                contributed to it. */}
            <TD warn={warningsFor(warnings, warnId.costPerHour())}>
              {fmtCostPerHour(totalCostUsd, recordingHours)}
            </TD>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/**
 * What a single call looks like at each stage. Every column after the calls
 * count is a per-*api*-call figure — divided by what was billed, so a stage that
 * repaired a bad answer shows the true cost of one response, not of one chunk.
 */
const TokenStatsTable = ({
  stages,
  isInherited,
  warnings,
}: {
  stages: StageCostReport[];
  isInherited: (stage: StageCostReport["stage"]) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // The tool columns exist only for a run whose stages offered tools: on every
  // other run they would be two columns of em dashes explaining nothing.
  const hasTools = stages.some(s => offeredTools(s).length > 0);
  const sum = (f: (s: StageCostReport) => number) => stages.reduce((acc, s) => acc + f(s), 0);
  const [popup, setPopup] = useState<DetailPopupContent | null>(null);
  return (
    <div className="mt-2 overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
      <table className="w-full border-collapse">
        <thead className="border-b border-outline-strong bg-muted-surface">
          <tr>
            <TH>{t("stage")}</TH>
            <TH>{t("logical_calls")}</TH>
            <TH hint={t("api_calls_hint")}>{t("api_calls")}</TH>
            <TH hint={t("in_per_api_call_hint")}>{t("in_per_api_call")}</TH>
            <TH hint={t("out_per_api_call_hint")}>{t("out_per_api_call")}</TH>
            <TH hint={t("max_out_hint")}>{t("max_out")}</TH>
            {hasTools && <TH hint={t("tool_rounds_hint")}>{t("tool_rounds")}</TH>}
            {hasTools && <TH hint={t("tool_calls_hint")}>{t("tool_calls")}</TH>}
          </tr>
        </thead>
        <tbody>
          {stages.map(stage => {
            const unitKey = LOGICAL_UNIT_KEY[stage.stage];
            const unit = unitKey ? t(`logical_unit_${unitKey}`) : "";
            // Absent on a row from before the counters existed — a mixed `--from`
            // re-run can put such a row beside fresh ones, so this is per-row, not
            // per-report. Zero reads the same as absent here: either way there is
            // no call to divide by, and every guard below keys off that.
            const apiCalls = stage.apiCalls ?? 0;
            const logicalCalls = stage.logicalCalls ?? 0;
            const maxOutputTokens = stage.maxOutputTokens ?? 0;
            // Reported on hover rather than flagged: what the repairs cost is the
            // Repair retries section's own subject, and a marker here would raise
            // the same run twice under two different counts.
            const retryRatio = logicalCalls > 0 ? apiCalls / logicalCalls : null;
            // Every figure past the call counts is per api call, so with no call
            // to divide by there is nothing to say: a row from before the
            // counters existed and a lift whose frontier was empty both land
            // here, and a zero would claim a measurement neither made.
            const perCall = (total: number) => (apiCalls > 0 ? fmtTokens(total / apiCalls) : "—");
            // Tool work is a stage total, not a per-call average: a mean over
            // chunks hides that most chunks called nothing.
            const offered = offeredTools(stage);
            // The figure opens the calls behind it; a report from before the log
            // keeps the bare figure.
            const log = stage.toolLog ?? [];
            const toolCell = (total: number) => {
              const figure = offered.length === 0 ? "—" : total.toLocaleString();
              if (log.length === 0) return figure;
              return (
                <OpenListButton
                  label={figure}
                  onClick={() =>
                    setPopup({
                      title: `${fmtStage(stage.stage)} — ${t("tool_calls")}`,
                      text: toolLogText(log, t),
                    })
                  }
                />
              );
            };
            const cap = stage.outputCapTokens ?? null;
            const capHint =
              cap === null || cap === 0
                ? t("max_out_no_cap_hint")
                : t("max_out_cap_hint", { cap: cap.toLocaleString() });
            return (
              <tr key={stage.stage} className="border-b border-outline last:border-b-0">
                {/* L2's row covers two different kinds of call. Said on the
                    stage cell, which is what the whole row is qualified by. */}
                <RowLabel
                  label={fmtStage(stage.stage)}
                  inherited={isInherited(stage.stage)}
                  hint={stage.stage === "lift-to-l2" ? t("l2_canonicalization_hint") : undefined}
                />
                <TD>
                  {logicalCalls.toLocaleString()} {unit}
                </TD>
                <TD>
                  <span
                    title={
                      retryRatio === null
                        ? undefined
                        : t("retry_ratio_hint", {
                            ratio: retryRatio.toFixed(2),
                          })
                    }
                  >
                    {apiCalls.toLocaleString()}
                  </span>
                </TD>
                <TD warn={warningsFor(warnings, warnId.inPerCall(stage.stage))}>
                  {perCall(stage.inputTokens)}
                </TD>
                <TD>{perCall(stage.outputTokens)}</TD>
                {/* The largest response of no responses is not zero tokens —
                    it is nothing, and says so. The share rides the figure
                    rather than the heading because the cap is per stage: how
                    close a row came is the one form of it that stays true for
                    that row whatever alias it called. The cap itself is on the
                    hover, not behind a "?" — a mark in every cell of one
                    numeric column is noise. */}
                <TD>
                  {apiCalls === 0 ? (
                    "—"
                  ) : (
                    <span title={capHint}>
                      {fmtTokens(maxOutputTokens)}
                      {cap !== null && cap > 0 && (
                        <span className="text-text-dim"> ({pct(maxOutputTokens / cap)})</span>
                      )}
                    </span>
                  )}
                </TD>
                {hasTools && (
                  <TD warn={warningsFor(warnings, warnId.toolBudget(stage.stage))}>
                    {/* An em dash for a stage that offered no tools; a zero
                        there would read as "offered and never used", which is a
                        different fact and has its own row in Tool use. */}
                    {/* Bare figure: the calls behind a round are the same list
                        the Tool calls column opens, so a second opener here
                        would only offer the reader the same panel twice. */}
                    {offered.length === 0 ? "—" : (stage.toolRounds ?? 0).toLocaleString()}
                  </TD>
                )}
                {hasTools && <TD>{toolCell(stage.toolCalls ?? 0)}</TD>}
              </tr>
            );
          })}
          {/* Sums, not averages: an average of per-call averages would weigh a
              one-call stage like a hundred-call one. Logical calls carry no unit
              because the stages count different things. */}
          <tr className="border-t border-outline-strong bg-muted-surface font-semibold">
            <TD>{t("total")}</TD>
            <TD>{sum(s => s.logicalCalls ?? 0).toLocaleString()}</TD>
            <TD>{sum(s => s.apiCalls ?? 0).toLocaleString()}</TD>
            <TD>{fmtTokens(sum(s => s.inputTokens))}</TD>
            <TD>{fmtTokens(sum(s => s.outputTokens))}</TD>
            <TD>—</TD>
            {hasTools && <TD>{sum(s => s.toolRounds ?? 0).toLocaleString()}</TD>}
            {hasTools && <TD>{sum(s => s.toolCalls ?? 0).toLocaleString()}</TD>}
          </tr>
        </tbody>
      </table>
      {popup && <DetailPopup content={popup} onClose={() => setPopup(null)} />}
    </div>
  );
};

/**
 * Tool calls as plain text for a popup: one block per chunk, each call in the
 * order it was made — its round, the tool and its arguments, and whether a
 * result came back. The result itself is not in the report.
 */
const toolLogText = (log: ToolCallLog[], t: Translate) => {
  const byChunk = new Map<number | null, ToolCallLog[]>();
  for (const call of log) {
    const chunk = call.chunk ?? null;
    byChunk.set(chunk, [...(byChunk.get(chunk) ?? []), call]);
  }
  return [...byChunk.entries()]
    .map(([chunk, calls]) =>
      [
        chunk === null ? "—" : `${t("retry_chunk")} ${chunk}`,
        // The round is a heading over the calls it made, not a label on each:
        // a round that fired three calls in parallel says so once, and the
        // repetition it replaces was the only thing making them look separate.
        ...calls.map((call, i) => {
          const round =
            i > 0 && calls[i - 1].round === call.round
              ? " ".repeat(`${t("tool_log_round")} ${call.round}`.length)
              : `${t("tool_log_round")} ${call.round}`;
          return (
            `  ${round}  ${call.tool} ${call.input}\n` +
            `    → ${call.error == null ? t("tool_log_ok") : `${t("tool_log_failed")}: ${call.error}`}`
          );
        }),
      ].join("\n"),
    )
    .join("\n\n");
};

/**
 * Which tools each stage put in front of the model, and how often it reached for
 * each: tools down, stages across, so one tool reads as a row across the
 * pipeline. Each cell is calls per logical call — the unit a tool budget is
 * spent in; a stage total says how much looking-up the whole stage did, the
 * average says whether a single chunk needed one lookup or nine — with the
 * tool's share of that stage's calls beside it.
 *
 * A tool at zero keeps its cell. It is the finding, not the absence of one: its
 * schema was sent with every request in the stage and bought nothing, and
 * (once the system prompt is cached) it sits in the cached prefix too.
 */
const ToolUseTable = ({ stages }: { stages: StageCostReport[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // Only the stages that offered anything become columns; a column of em dashes
  // for the annotator would widen the table to say it has no tools.
  const columns = stages.filter(stage => offeredTools(stage).length > 0);
  // One row per tool name across every stage, busiest first, so the table opens
  // on what the run actually leaned on.
  const byTotal = new Map<string, number>();
  for (const stage of columns) {
    for (const [tool, calls] of offeredTools(stage)) {
      byTotal.set(tool, (byTotal.get(tool) ?? 0) + calls);
    }
  }
  const tools = [...byTotal.entries()]
    .sort(([aName, a], [bName, b]) => (b - a !== 0 ? b - a : aName.localeCompare(bName)))
    .map(([tool]) => tool);
  const [popup, setPopup] = useState<DetailPopupContent | null>(null);
  if (tools.length === 0) return <p className="text-sm text-text-mid">{t("no_tools_offered")}</p>;
  return (
    <div className="mt-2 overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
      <table className="w-full border-collapse">
        <thead className="border-b border-outline-strong bg-muted-surface">
          <tr>
            <TH>{t("tool_name")}</TH>
            {columns.map(stage => (
              <TH key={stage.stage}>{fmtStage(stage.stage)}</TH>
            ))}
          </tr>
        </thead>
        <tbody>
          {tools.map(tool => (
            <tr key={tool} className="border-b border-outline last:border-b-0">
              <td className="px-2 py-1.5 font-mono text-sm text-text">{tool}</td>
              {columns.map(stage => {
                const calls = stage.tools?.[tool];
                // Absent, not zero: this stage never offered this tool, which is
                // not the same as offering it and never calling it.
                if (calls === undefined) return <TD key={stage.stage}>—</TD>;
                // Of this stage's tool calls, not the run's: the question is which
                // tool a stage leaned on, and stages differ in what they offered.
                const stageCalls = stage.toolCalls ?? 0;
                const figure = calls.toLocaleString();
                const share = stageCalls > 0 ? pct(calls / stageCalls) : null;
                const log = (stage.toolLog ?? []).filter(call => call.tool === tool);
                return (
                  <TD key={stage.stage}>
                    <span className={calls === 0 ? "text-text-dim" : undefined}>
                      {log.length === 0 ? (
                        <>
                          {figure}
                          {share !== null && <span className="text-text-dim"> ({share})</span>}
                        </>
                      ) : (
                        /* The share rides inside the opener rather than beside
                           it: figure and share are one reading of the cell, and
                           a button holding half of it would split them. */
                        <OpenListButton
                          label={share === null ? figure : `${figure} (${share})`}
                          onClick={() =>
                            setPopup({
                              title: `${fmtStage(stage.stage)} — ${tool}`,
                              text: toolLogText(log, t),
                            })
                          }
                        />
                      )}
                    </span>
                  </TD>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {popup && <DetailPopup content={popup} onClose={() => setPopup(null)} />}
    </div>
  );
};

/**
 * Just enough of `t` for the row builders below, which only ever look a key up.
 * Narrower than the real thing on purpose: `t`'s own type is deep enough that
 * naming it here does not typecheck.
 */
type Translate = (key: AnalysisReportKey) => string;

/**
 * One row of the failures table, flattened out of the two records that feed it
 * so the table itself renders cells rather than re-deriving which kind of
 * failure it is holding.
 */
type FailureRow = {
  key: string;
  stage: string;
  /** Whether the stage that failed this call was inherited rather than re-run. */
  inherited: boolean;
  /** What the failed call was working on: a lift's chunk, a vision call's click. */
  unit: string;
  attempt: string;
  preview: string;
  popup: DetailPopupContent;
};

/**
 * The annotator's dead clicks as failure rows. They are not retries — a vision
 * call that comes back unusable kills its click outright — so they carry no
 * attempt, and the kind that killed them leads the preview where a lift row
 * leads with an error count.
 */
const visionFailureRows = (
  annotation: AnnotationReport | null,
  t: Translate,
  isInherited: (stage: AnalyzerStage) => boolean,
): FailureRow[] =>
  (annotation?.visionFailures ?? []).map(failure => {
    const reason = t(VISION_FAILURE_KIND_LABEL[failure.kind]);
    return {
      key: `vision-${failure.step}`,
      stage: fmtStage("annotator"),
      inherited: isInherited("annotator"),
      unit: `${t("vision_failure_click")} ${failure.step}`,
      attempt: "—",
      preview: `${reason} — ${failure.detail}`,
      popup: {
        title: `${t("vision_failure_click")} ${failure.step} — ${reason}`,
        text: failure.detail,
      },
    };
  });

/** The repair retries as failure rows: one per failed `call_json` attempt. */
const retryRows = (
  retries: StageRetryReport[],
  t: Translate,
  isInherited: (stage: AnalyzerStage) => boolean,
): FailureRow[] =>
  retries.flatMap(stage =>
    stage.retries.map((retry, i) => ({
      key: `${stage.stage}-${i}`,
      stage: fmtStage(stage.stage),
      inherited: isInherited(stage.stage),
      // The canonicalization pass is one call over the whole layer rather than a
      // chunk of anything, and a report predating the field has no chunk to name.
      unit: retry.chunk == null ? "—" : `${t("retry_chunk")} ${retry.chunk}`,
      attempt: String(retry.attempt),
      preview: `${retry.errors.length} — ${retry.errors[0] ?? ""}`,
      popup: {
        title: `${fmtStage(stage.stage)} — ${t("retry_attempt").toLowerCase()} ${retry.attempt}`,
        text: retry.errors.join("\n\n"),
      },
    })),
  );

/**
 * Every LLM response the run could not use, one row per failure: which stage,
 * what it was working on, which attempt within its logical call, and the
 * evidence — behind a popup, since a validation error list can run long.
 *
 * The annotator's vision failures sit here rather than in its own section: both
 * kinds are a response that came back unusable, and reading them apart hid that
 * the annotator's cost the run a click while a lift's cost it only a retry.
 */
const FailuresTable = ({ rows }: { rows: FailureRow[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const [popup, setPopup] = useState<DetailPopupContent | null>(null);
  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{t("stage")}</TH>
              <TH>{t("retry_unit")}</TH>
              <TH>{t("retry_attempt")}</TH>
              <TH>{t("retry_errors")}</TH>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.key} className="border-b border-outline last:border-b-0">
                <RowLabel label={row.stage} inherited={row.inherited} />
                <TD>{row.unit}</TD>
                <TD>{row.attempt}</TD>
                <TD>
                  <DetailCell
                    preview={row.preview}
                    openLabel={t("view_detail")}
                    onOpen={() => setPopup(row.popup)}
                  />
                </TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {popup && <DetailPopup content={popup} onClose={() => setPopup(null)} />}
    </>
  );
};

/** Per-stage cost and time, with the call/token breakdown behind it. */
const CostsSection = ({
  stages,
  totalCostUsd,
  totalDurationMs,
  recordingHours,
  isInherited,
  warnings,
}: {
  stages: StageCostReport[];
  totalCostUsd: number;
  totalDurationMs: number | null;
  recordingHours: number;
  isInherited: (stage: StageCostReport["stage"]) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (stages.length === 0) return <EmptySection title={t("cost_and_tokens")} />;
  // Every column of the token table derives from the call counts, so a report
  // from before the counters existed has nothing to put in it. Its heading stays
  // regardless, saying so: a subsection that simply vanished would be
  // indistinguishable from a broken one. The cost table above stands on its own
  // and still reports either way.
  const hasCallStats = stages.some(s => (s.apiCalls ?? 0) > 0);
  const hasTools = stages.some(s => offeredTools(s).length > 0);
  return (
    <Section title={t("cost_and_tokens")} warnings={warnings}>
      <CostTimeTable
        stages={stages}
        totalCostUsd={totalCostUsd}
        totalDurationMs={totalDurationMs}
        recordingHours={recordingHours}
        isInherited={isInherited}
        warnings={warnings}
      />
      <SubHeading>{t("token_stats")}</SubHeading>
      {hasCallStats ? (
        <TokenStatsTable stages={stages} isInherited={isInherited} warnings={warnings} />
      ) : (
        <p className="text-sm text-text-mid">{t("stats_predate_report")}</p>
      )}
      {/* Only where a stage offered tools: on every other run the heading would
          introduce a sentence saying nothing happened. The two columns in the
          table above are the summary; this is the breakdown behind them, so it
          sits directly under it rather than in a section of its own. */}
      {hasTools && (
        <>
          <SubHeading hint={t("tool_use_hint")}>{t("tool_use")}</SubHeading>
          <ToolUseTable stages={stages} />
        </>
      )}
    </Section>
  );
};

/**
 * What the run had to repair on its way through. Cross-cutting rather than
 * per-stage, and read before the stages themselves: whether a run repaired
 * itself at all colours how its stage detail should be read.
 *
 * On a resumed run the inherited stages' failures are still listed — they are
 * repairs the artifacts this analysis stands on actually needed — and marked, so
 * that an operator re-running a stage *because* of what they see here can tell
 * the failures they just reproduced from the ones they inherited.
 */
const RetriesSection = ({
  retries,
  annotation,
  isInherited,
}: {
  retries: StageRetryReport[];
  annotation: AnnotationReport | null;
  isInherited: (stage: AnalyzerStage) => boolean;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // Annotator first, as the pipeline runs it: a click that never got a label is
  // upstream of every chunk the lifts then repaired.
  const rows = [
    ...visionFailureRows(annotation, t, isInherited),
    ...retryRows(retries, t, isInherited),
  ];
  // Every row is itself the thing to warn about — an unusable response — so the
  // count in the title bar is the table's own length rather than a threshold
  // read off it. Derived here rather than in the warnings module for the same
  // reason: there is no knob to tune.
  const warnings: Warning[] = rows.map(row => ({
    id: warnId.failedCall(row.key),
    key: "warning_failed_call",
    params: { stage: row.stage },
  }));
  return (
    <Section title={t("repair_retries")} hint={t("repair_retries_hint")} warnings={warnings}>
      {/* No rows is a clean run (or a report predating the record — the call
          counts in the section above tell those apart), so it reads as a figure
          rather than as a table of nothing. */}
      {rows.length > 0 ? (
        <FailuresTable rows={rows} />
      ) : (
        <p className="text-sm text-text-mid">{t("no_retries_recorded")}</p>
      )}
    </Section>
  );
};

/** Shared partition diagnostics followed by each stage's actual work. */
const ChunkingSection = ({ chunking }: { chunking: StageChunkingReport[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const shared = hasSharedPartition(chunking);
  const cutEntries = shared ? chunking.slice(0, 1) : chunking;
  const warnings: Warning[] = [
    ...cutEntries
      .filter(entry => entry.chunking.maxSpanCuts > 0)
      .map(entry => ({
        id: warnId.spanLimitCuts(shared ? "shared" : entry.stage),
        key:
          entry.chunking.maxSpanCuts === 1
            ? ("warning_span_limit_cuts_one" as const)
            : ("warning_span_limit_cuts" as const),
        params: {
          stage: shared ? t("shared_partition") : fmtStage(entry.stage),
          count: entry.chunking.maxSpanCuts,
        },
      })),
    ...chunking.flatMap(entry =>
      entry.chunking.splits.map((split, index) => ({
        id: warnId.truncationSplit(entry.stage + "-" + index),
        key: "warning_truncation_split" as const,
        params: {
          stage: fmtStage(entry.stage),
          chunk: split.chunk,
          left: split.left,
          right: split.right,
        },
      })),
    ),
  ];
  return (
    <Section
      title={t("virtual_recordings")}
      hint={t("virtual_recordings_hint")}
      warnings={warnings}
    >
      <VirtualRecordingsContent chunking={chunking} />
    </Section>
  );
};

/** A bar segment's fixed face: its name, its colour, and its legend hint. */
type SegmentStyle = {
  label: string;
  className: string;
  hint: string;
};

type BarSegment = SegmentStyle & { seconds: number };

/**
 * Per-level share of the recording as one stacked bar each: structured (adopted
 * by the level above; every root at L4) / orphaned / uncovered, all relative to
 * the total recording length. The L4 bar additionally carves the pruned
 * instances out of "uncovered" — pruning removed them from the level entirely,
 * so the level's own coverage no longer knows about them.
 */
/**
 * The orphans of one level, in the order they ran: duration and goal, nothing
 * else. This answers only "what did this level discard" — a node's id, name and
 * attributes are the analytics blob's business, not this list's.
 */
const OrphanListPopup = ({
  depth,
  orphans,
  onClose,
}: {
  depth: number;
  orphans: OrphanNode[];
  onClose: () => void;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    <Popup title={t("orphan_list_title", { depth, total: orphans.length })} onClose={onClose}>
      <div className="mt-3 flex flex-col divide-y divide-outline overflow-y-auto rounded-xl border border-outline bg-muted-surface/30 text-xs leading-5">
        {orphans.map((orphan, i) => (
          <div key={i} className="flex gap-3 px-3 py-2">
            <span className="w-16 shrink-0 tabular-nums text-text-mid">
              {formatDuration(orphan.durationS)}
            </span>
            <span className={orphan.goal ? "break-words" : "break-words text-text-dim"}>
              {orphan.goal || t("orphan_list_no_goal")}
            </span>
          </div>
        ))}
      </div>
    </Popup>
  );
};

const TimeAccounting = ({
  levels,
  pruning,
  warnings,
}: {
  levels: LevelReport[];
  pruning: PruneReport | null | undefined;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // Which level's orphan list is open, by depth — at most one at a time.
  const [openOrphans, setOpenOrphans] = useState<number | null>(null);
  // Style only — each level supplies its own seconds when its bar is built.
  // Declared in bar order, which is also the legend's order.
  const styles = {
    structured: {
      label: t("structured_time"),
      className: "bg-success/70",
      hint: t("structured_time_hint"),
    },
    orphaned: {
      label: t("orphaned_time"),
      className: "bg-warning/70",
      hint: t("orphaned_time_hint"),
    },
    prunedShort: {
      label: t("pruned_too_short"),
      className: "bg-danger/70",
      hint: t("pruned_too_short_hint", {
        duration: formatDuration(pruning?.minDurationS ?? 0),
      }),
    },
    prunedSingle: {
      label: t("pruned_single_child"),
      className: "bg-danger/35",
      hint: t("pruned_single_child_hint"),
    },
    uncovered: {
      label: t("uncovered_time"),
      className: "bg-surface",
      hint: t("uncovered_time_hint"),
    },
  } satisfies Record<string, SegmentStyle>;

  const prunedCount = (pruning?.tooShortCount ?? 0) + (pruning?.singleChildCount ?? 0);
  const pruneCaption = (total: number) =>
    pruning &&
    prunedCount > 0 &&
    t("prune_caption", {
      count: prunedCount,
      tooShort: pruning.tooShortCount,
      tooShortDetail: `${formatDuration(pruning.tooShortDurationS)}, ${sharePct(pruning.tooShortDurationS, total)}`,
      singleChild: pruning.singleChildCount,
      singleChildDetail: `${formatDuration(pruning.singleChildDurationS)}, ${sharePct(pruning.singleChildDurationS, total)}`,
    });

  // The bars are built before anything renders so the legend can be drawn from
  // what they actually contain. A swatch for a segment no bar has is a colour
  // the reader will hunt for and never find — and worse for the pruned pair,
  // whose hint quotes a threshold: a run that pruned nothing carries no prune
  // report, so the legend would state a floor this run never applied.
  const rows = levels.flatMap(level => {
    const structuredS = level.structuredDurationS ?? 0;
    const uncoveredS = level.uncoveredDurationS ?? 0;
    const total = levelTotalS(level);
    if (total <= 0) return [];
    const isInstances = level.depth === 4;
    const prunedShortS = isInstances ? (pruning?.tooShortDurationS ?? 0) : 0;
    const prunedSingleS = isInstances ? (pruning?.singleChildDurationS ?? 0) : 0;
    // The pruning warning covers the pair of prune segments, so it rides
    // whichever of them the bar actually draws first.
    const pruneWarn = isInstances ? warningsFor(warnings, warnId.pruning()) : [];
    const segments = (
      [
        {
          ...styles.structured,
          seconds: structuredS,
          warn: warningsFor(warnings, warnId.structured(level.depth)),
        },
        {
          ...styles.orphaned,
          seconds: orphanedDurationS(level),
          warn: warningsFor(warnings, warnId.orphaned(level.depth)),
        },
        {
          ...styles.prunedShort,
          seconds: prunedShortS,
          warn: prunedShortS > 0 ? pruneWarn : [],
        },
        {
          ...styles.prunedSingle,
          seconds: prunedSingleS,
          warn: prunedShortS > 0 ? [] : pruneWarn,
        },
        {
          ...styles.uncovered,
          seconds: Math.max(uncoveredS - prunedShortS - prunedSingleS, 0),
          warn: [],
        },
      ] satisfies (BarSegment & { warn: Warning[] })[]
    ).filter(s => s.seconds > 0);
    return [{ level, total, isInstances, segments }];
  });
  const drawn = new Set(rows.flatMap(row => row.segments.map(s => s.label)));

  return (
    <div className="mt-3">
      <div className="mb-5 flex flex-wrap gap-x-3 gap-y-1">
        {Object.values(styles)
          .filter(({ label }) => drawn.has(label))
          .map(({ label, className, hint }) => (
            <span key={label} className="inline-flex items-center gap-1.5 text-xs text-text-mid">
              <span
                className={`h-2.5 w-2.5 rounded-sm border border-outline-strong ${className}`}
              />
              {label}
              <HintTip text={hint} />
            </span>
          ))}
      </div>
      <div className="flex flex-col gap-2.5">
        {rows.map(({ level, total, isInstances, segments }) => (
          // The label is now just "L1", so it sits in a narrow column right
          // against the bar; the bar itself is capped rather than stretched to
          // the width of the tab.
          <div key={level.depth} className="flex items-start gap-2">
            <span className="flex w-11 shrink-0 items-center text-xs font-medium leading-4 text-text-mid">
              {`L${level.depth}`}
            </span>
            <div className="max-w-2xl flex-1">
              <div className="flex h-4 overflow-hidden rounded-md border border-outline-strong">
                {segments.map(s => (
                  <div
                    key={s.label}
                    className={s.className}
                    style={{ width: `${(s.seconds / total) * 100}%` }}
                    title={`${s.label}: ${formatDuration(s.seconds)} (${sharePct(s.seconds, total)})`}
                  />
                ))}
              </div>
              {/* Each figure centered under its own segment, so the colour that
                    names it (via the legend) sits directly above — the word label
                    is no longer repeated per bar. A flagged segment carries its
                    marker here, beside the figure the warning is about, rather
                    than out on the row's label where the reader cannot tell
                    which share it means. */}
              <div className="mt-0.5 flex">
                {segments.map(s => (
                  <span
                    key={s.label}
                    className="flex flex-wrap items-center justify-center gap-1 px-0.5 text-center text-[11px] text-text-dim tabular-nums"
                    style={{ width: `${(s.seconds / total) * 100}%` }}
                    title={`${s.label}: ${formatDuration(s.seconds)} (${sharePct(s.seconds, total)})`}
                  >
                    {`${formatDuration(s.seconds)} (${sharePct(s.seconds, total)})`}
                    <WarnTip warnings={s.warn} />
                  </span>
                ))}
              </div>
              {isInstances && pruneCaption(total) && (
                <p className="mt-0.5 text-[11px] text-text-mid">{pruneCaption(total)}</p>
              )}
            </div>
            {/* Beside the bar rather than under it: the bar is capped at
                max-w-2xl, so this column is space the row already has. The list
                is always empty at L4, whose nodes are roots and cannot orphan. */}
            {level.orphans && level.orphans.length > 0 && (
              <OpenListButton
                label={t("orphan_list_button", { count: level.orphans.length })}
                onClick={() => setOpenOrphans(level.depth)}
              />
            )}
          </div>
        ))}
      </div>
      {openOrphans !== null && (
        <OrphanListPopup
          depth={openOrphans}
          orphans={levels.find(l => l.depth === openOrphans)?.orphans ?? []}
          onClose={() => setOpenOrphans(null)}
        />
      )}
    </div>
  );
};

/**
 * A dropdown picker styled as a button: a native `<select>` (so the menu, keyboard,
 * and accessibility come for free) with a chevron overlaid on the right.
 */
const PickerSelect = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) => (
  <div className="relative inline-flex">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="cursor-pointer appearance-none rounded-lg border border-outline-strong bg-surface py-1 pl-3 pr-8 text-xs font-medium text-text-mid transition-colors hover:bg-muted-surface/50 focus:outline-none focus:ring-1 focus:ring-info/40"
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-dim" />
  </div>
);

/**
 * Rough per-level histograms: node duration (15 equal-width bins up to the
 * level's p95, plus an overflow bar) or children per node (one bar per exact
 * child count). Level and metric are each picked from a dropdown; details sit in
 * each bar's tooltip.
 */
const Distributions = ({ levels }: { levels: LevelReport[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const [depth, setDepth] = useState(levels[0]?.depth ?? 1);
  const [metric, setMetric] = useState<"duration" | "children">("duration");
  const level = levels.find(l => l.depth === depth) ?? levels[0];
  if (!level) return null;

  const binWidthS = level.durationHistogram?.binWidthS ?? 0;
  const durationCounts = level.durationHistogram?.counts ?? [];
  const bars =
    metric === "duration"
      ? durationCounts.map((count, i) => ({
          count,
          label:
            i === durationCounts.length - 1
              ? `> ${formatDuration(binWidthS * i)}`
              : `${formatDuration(binWidthS * i)} – ${formatDuration(binWidthS * (i + 1))}`,
        }))
      : (level.childrenHistogram ?? []).map((count, i) => ({
          count,
          label: `${i}`,
        }));
  const maxCount = Math.max(...bars.map(b => b.count), 1);

  // The axis marks where each bin *starts*. A child count is its own label and
  // narrow enough to print under every bin; a duration ("1m 36s") is several
  // times a bin wide, so it is drawn every third one and allowed to overhang the
  // blank neighbours either side.
  const tickEvery = metric === "duration" ? 3 : 1;
  const tickLabel = (i: number) => (metric === "duration" ? formatDuration(binWidthS * i) : `${i}`);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <PickerSelect
          value={String(depth)}
          onChange={value => setDepth(Number(value))}
          options={levels.map(l => ({
            value: String(l.depth),
            label: `L${l.depth}`,
          }))}
        />
        <PickerSelect
          value={metric}
          onChange={value => setMetric(value as "duration" | "children")}
          options={[
            { value: "duration", label: t("metric_duration") },
            { value: "children", label: t("metric_children") },
          ]}
        />
      </div>
      {bars.length === 0 ? (
        <p className="mt-3 text-sm text-text-mid">{t("not_recorded")}</p>
      ) : (
        // Each bin is a fixed width rather than a share of the row, so a bar means
        // the same thing at every setting: the chart grows and shrinks with the
        // number of bins instead of stretching them to a constant total width.
        // `w-fit` keeps the axis labels below flush with whatever width that is.
        <div className="mt-3 w-fit">
          <div className="flex h-36 items-end gap-px rounded-lg border border-outline-strong bg-surface p-2 shadow-sm">
            {bars.map((bar, i) => (
              <div key={i} className="flex h-full w-6 shrink-0 flex-col justify-end">
                {/* The drawn bar alone is the hover target: the empty space above
                    it is not part of the bin, so it stays inert. An empty bin has
                    no bar to hover, and so no tooltip. */}
                {bar.count > 0 ? (
                  <Tip
                    place="above"
                    width={128}
                    className="block w-full rounded-t-sm bg-info/60"
                    style={{ height: `${(bar.count / maxCount) * 100}%` }}
                    panel={
                      <span className="text-center">
                        <span className="block font-semibold tabular-nums">
                          {`${bar.count} ${t("nodes").toLowerCase()}`}
                        </span>
                        <span className="block text-white/70 tabular-nums">{bar.label}</span>
                      </span>
                    }
                  />
                ) : (
                  <div className="w-full" />
                )}
              </div>
            ))}
          </div>
          {/* Ticks sit under their own bin, padded to match the chart's `p-2` so
              they line up with it. Duration labels are far wider than a bin, so
              only every third is drawn; the exact bin range is in the tooltip. */}
          <div className="mt-1 flex gap-px px-2">
            {bars.map((_, i) => (
              // Keyed by position, not label: a level whose nodes all have zero
              // duration bins to a zero-width bin, and every label collapses to
              // the same string.
              <span
                key={i}
                className="w-6 shrink-0 whitespace-nowrap text-center text-[10px] text-text-dim tabular-nums"
              >
                {i % tickEvery === 0 ? tickLabel(i) : ""}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Full-fidelity per-level node stats from the analysis run (all depths). */
const LevelsSection = ({
  levels,
  pruning,
  isLevelInherited,
  warnings,
}: {
  levels: LevelReport[];
  pruning?: PruneReport | null;
  /**
   * Whether a level's nodes came from the base run. The stats beside the chip
   * are always this run's arithmetic — the Adapter recomputes every level from
   * the reassembled store — so the chip marks where the nodes came from, which
   * is the thing the numbers cannot say.
   */
  isLevelInherited: (depth: number) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // The "Avg duration" column heading is the app-wide key, not a report one.
  const { t: tApp } = useTranslation();
  if (levels.length === 0) return <EmptySection title={t("lift_stages")} nested />;
  return (
    <Section title={t("lift_stages")} nested warnings={warnings}>
      <div className="overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{t("level")}</TH>
              <TH>{t("nodes")}</TH>
              <TH>{t("avg_children")}</TH>
              <TH>{t("single_child_nodes")}</TH>
              <TH>{tApp("duration_avg")}</TH>
              <TH hint={t("fragmentation_hint")}>{t("fragmentation")}</TH>
              <TH>{t("avg_apps_per_node")}</TH>
            </tr>
          </thead>
          <tbody>
            {levels.map(level => {
              // No node at this level: every derived figure is an em dash rather
              // than a 0 / 0.00 that would read as a real, empty-looking level.
              if (level.nodeCount === 0) {
                return (
                  <tr key={level.depth} className="border-b border-outline last:border-b-0">
                    <RowLabel label={`L${level.depth}`} inherited={isLevelInherited(level.depth)} />
                    <TD>{level.nodeCount}</TD>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                  </tr>
                );
              }
              return (
                <tr key={level.depth} className="border-b border-outline last:border-b-0">
                  <RowLabel label={`L${level.depth}`} inherited={isLevelInherited(level.depth)} />
                  <TD>{level.nodeCount}</TD>
                  <TD warn={warningsFor(warnings, warnId.avgChildren(level.depth))}>
                    {`${fmtAvg(level.avgChildrenPerParent)} (max ${level.maxChildrenPerParent ?? 0})`}
                  </TD>
                  <TD>{level.singleChildNodes}</TD>
                  <TD>{formatDuration(level.durationMeanS)}</TD>
                  <TD warn={warningsFor(warnings, warnId.fragmentation(level.depth))}>
                    {fmtFragmentation(level.fragmentationScore ?? 0)}
                  </TD>
                  <TD warn={warningsFor(warnings, warnId.avgApps(level.depth))}>
                    {level.avgAppsPerNode != null
                      ? `${fmtAvg(level.avgAppsPerNode)} (max ${level.maxAppsPerNode ?? 0})`
                      : "—"}
                  </TD>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <SubHeading>{t("time_accounting")}</SubHeading>
      <TimeAccounting levels={levels} pruning={pruning} warnings={warnings} />
      <SubHeading>{t("distributions")}</SubHeading>
      <Distributions levels={levels} />
    </Section>
  );
};

/**
 * What the canonicalization pass did to every object name it was handed, one row
 * each: the merges first — its actual work, and what the two counts above only
 * summarize — then the names it left alone, which are most of them and the reason
 * this is a disclosure rather than a table on the page.
 *
 * Nothing to disclose for a report whose L2 fragment predates the record: the
 * counts above already say so, and an empty table would read as a pass that saw
 * no names.
 */
const MergeGroups = ({ groups }: { groups: ObjectGroup[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (groups.length === 0) return null;
  const merged = groups.filter(group => group.aliases.length > 0);
  const aliasCount = merged.reduce((sum, group) => sum + group.aliases.length, 0);
  // Merges first, each with its aliases; the untouched names keep the order the
  // recording produced them in.
  const ordered = [...merged, ...groups.filter(group => group.aliases.length === 0)];
  // The window only tells the reader something when there was more than one to
  // be in: a single-window pass compared every name with every other.
  const showWindow = new Set(groups.map(group => group.window)).size > 1;
  return (
    <details className="group/merges mt-2 rounded-xl border border-outline-strong bg-surface shadow-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-dim transition-transform duration-200 group-open/merges:rotate-90" />
        <span className="text-xs font-medium text-text-mid">
          {t("object_merges_summary", {
            merged: aliasCount,
            total: groups.length + aliasCount,
          })}
        </span>
      </summary>
      <div className="overflow-x-auto border-t border-outline">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{t("canonical_name")}</TH>
              <TH>{t("merged_from")}</TH>
              {showWindow && <TH hint={t("window_hint")}>{t("window")}</TH>}
            </tr>
          </thead>
          <tbody>
            {ordered.map(group => (
              <tr
                key={`${group.window}-${group.canonical}`}
                className="border-b border-outline last:border-b-0"
              >
                <TD>{group.canonical}</TD>
                <TD>{group.aliases.length > 0 ? <NameChips names={group.aliases} /> : "—"}</TD>
                {showWindow && <TD>{group.window + 1}</TD>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
};

/**
 * The objects two or more L4 instances have in common — the thread between two
 * runs, if there is one. Instances carry no objects themselves, so these are
 * folded up from their steps.
 */
const InstanceOverlap = ({ overlap }: { overlap: ObjectOverlap[] | null }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // A stored blob from before this was computed shares nothing *that we know of* —
  // distinct from a run that correlated its instances and found nothing in common.
  if (overlap === null) return <p className="text-sm text-text-mid">{t("section_not_recorded")}</p>;
  if (overlap.length === 0)
    return <p className="text-sm text-text-mid">{t("no_shared_objects")}</p>;
  return (
    <ul className="flex flex-col gap-0.5">
      {overlap.map(entry => (
        <li key={entry.object} className="text-sm text-text-mid">
          {t("object_shared_by_instances", {
            object: entry.object,
            instances: entry.instanceCount,
          })}
        </li>
      ))}
    </ul>
  );
};

/**
 * The business-object layer: the clipboard links recovered at L1, what the L2
 * canonicalization pass collapsed, how densely objects sit on the nodes at the
 * two levels that carry them, and what the L4 instances share.
 */
const ObjectsSection = ({
  objects,
  recordingHours,
  isLevelInherited,
  warnings,
}: {
  objects: ObjectsReport;
  recordingHours: number;
  /** Whether a level's nodes — and so the objects bubbled onto them — were
   *  produced by an inherited lift. */
  isLevelInherited: (depth: number) => boolean;
  warnings: Warning[];
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const { clipboard, canonicalization, levels, instanceOverlap } = objects;
  return (
    <Section title={t("objects_stats")} warnings={warnings}>
      <SubHeading first>{t("clipboard_links")}</SubHeading>
      <div className="grid grid-cols-4 gap-3">
        <Stat label={t("clipboard_pairs")} value={String(clipboard.pastesPaired)} />
        <Stat
          label={t("clipboard_pairs_per_hour")}
          value={fmtRate(perHour(clipboard.pastesPaired, recordingHours))}
          warn={warningsFor(warnings, warnId.clipboardPairsPerHour())}
        />
      </div>

      <SubHeading>{t("canonicalization")}</SubHeading>
      <div className="grid grid-cols-4 gap-3">
        <Stat
          label={t("objects_in")}
          // Only the L2 stage's fragment carries this; an older run reports the
          // "out" side alone rather than implying nothing came in.
          value={
            canonicalization.distinctIn === null
              ? t("not_recorded")
              : String(canonicalization.distinctIn)
          }
        />
        <Stat label={t("objects_out")} value={String(canonicalization.distinctOut)} />
        {/* The "out" count rated against the recording. A recording with no
            length measured nothing per hour, and a 0 would read as a run that
            found no objects. */}
        <Stat
          label={t("business_objects_per_hour")}
          value={fmtRate(businessObjectsPerHour(objects, recordingHours))}
          warn={warningsFor(warnings, warnId.businessObjectsPerHour())}
        />
      </div>
      <MergeGroups groups={canonicalization.groups} />

      <div className="mt-4 overflow-x-auto rounded-xl border border-outline-strong bg-surface shadow-sm">
        <table className="w-full border-collapse">
          <thead className="border-b border-outline-strong bg-muted-surface">
            <tr>
              <TH>{t("level")}</TH>
              <TH>{t("distinct_objects")}</TH>
              <TH>{t("avg_objects_per_node")}</TH>
              <TH>{t("zero_object_nodes")}</TH>
            </tr>
          </thead>
          <tbody>
            {levels.map(level => (
              <tr key={level.depth} className="border-b border-outline last:border-b-0">
                <RowLabel label={`L${level.depth}`} inherited={isLevelInherited(level.depth)} />
                {/* No node at this level: nothing carried an object, referenced
                    none, or averaged any — every figure is an em dash rather
                    than a 0 that would read as a level naming nothing. Matches
                    the level table's own empty row. */}
                {level.nodeCount === 0 ? (
                  <>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                    <TD>{"—"}</TD>
                  </>
                ) : (
                  <>
                    <TD>{level.distinctObjects}</TD>
                    <TD>{fmtAvg(level.avgObjectsPerNode)}</TD>
                    <TD>
                      {`${level.zeroObjectNodes} (${sharePct(level.zeroObjectNodes, level.nodeCount)})`}
                    </TD>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* The list is the stat, so its heading carries the marker. */}
      <SubHeading>
        <span className="inline-flex items-center gap-1">
          {t("instance_overlap")}
          <WarnTip warnings={warningsFor(warnings, warnId.instanceOverlap())} />
        </span>
      </SubHeading>
      <InstanceOverlap overlap={instanceOverlap} />
    </Section>
  );
};

/**
 * The per-stage diagnostics, in pipeline order: cleanup ingests the recording,
 * the annotator reads what it left, the lift stages build the hierarchy on top.
 *
 * They sit under one heading because that order is the point — each stage is
 * reported against what the one before it produced — and because three of the
 * report's boxes being a stage, and the rest not, is otherwise left for the
 * reader to notice from the titles alone.
 */
const StagesSection = ({
  report,
  recordingHours,
  isInherited,
  levelInherited,
  warnings,
}: {
  report: Extract<AnalysisReport, { version: "V1" }>;
  recordingHours: number;
  isInherited: (stage: AnalyzerStage) => boolean;
  levelInherited: (depth: number) => boolean;
  warnings: ReportWarnings;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    // This section holds nothing of its own, so its badge is what its stages
    // carry: a reader scanning the collapsed report should not have to open a
    // clean-looking box to discover the warnings are one level further in.
    <Section
      title={t("stages")}
      holdsSections
      warnings={[...warnings.cleanup, ...warnings.annotation, ...warnings.levels]}
    >
      <CleanupSection
        cleanup={report.cleanup}
        inherited={isInherited("cleanup")}
        warnings={warnings.cleanup}
      />
      {/* The click/screenshot pairing happens in cleanup, so the windows the
          annotator's delays are measured against are reported there — a run
          with no cleanup section simply quotes no window. */}
      <AnnotationSection
        annotation={report.annotation}
        thresholds={report.cleanup?.thresholds ?? null}
        recordingHours={recordingHours}
        inherited={isInherited("annotator")}
        warnings={warnings.annotation}
      />
      <LevelsSection
        levels={report.levels}
        pruning={report.pruning}
        isLevelInherited={levelInherited}
        warnings={warnings.levels}
      />
    </Section>
  );
};

/**
 * The report's sections, in reading order: what the run cost as a whole and what
 * it had to repair, then the stages that spent it, and finally the two
 * cross-cutting views of what came out, naming and objects.
 *
 * The vocabulary is derived from the stored analysis rather than the report, so
 * it survives a run that produced no report at all.
 */
const ReportBody = ({
  report,
  vocab,
  recordingHours,
}: {
  report: AnalysisReport;
  vocab: VocabReport;
  recordingHours: number;
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  // Raised in one pass over the whole report rather than per section: a section's
  // title bar counts what opening it will show, and two counts derived
  // independently are two counts that can disagree.
  const warnings = computeReportWarnings(report, vocab, recordingHours);
  // Which stages this run executed and which it copied, asked once and handed to
  // every table: the answer is a fact about the report, and a section deriving
  // it for itself is a section that can mark a different set of rows than its
  // neighbour. `null` on a full run and on a stored report from before the
  // lineage was recorded — in both cases nothing is marked.
  const resumedFrom = report.version === "V1" ? report.resumedFrom : null;
  const isInherited = (stage: AnalyzerStage) => isStageInherited(resumedFrom, stage);
  const levelInherited = (depth: number) => isLevelInherited(resumedFrom, depth);
  switch (report.version) {
    case "V1":
      return (
        <>
          <CostsSection
            stages={report.stages}
            totalCostUsd={report.totalCostUsd}
            totalDurationMs={report.totalDurationMs}
            recordingHours={recordingHours}
            isInherited={isInherited}
            warnings={warnings.costs}
          />
          <RetriesSection
            retries={report.retries}
            annotation={report.annotation}
            isInherited={isInherited}
          />
          <ChunkingSection chunking={report.chunking} />
          <StagesSection
            report={report}
            recordingHours={recordingHours}
            isInherited={isInherited}
            levelInherited={levelInherited}
            warnings={warnings}
          />
          <VocabSection vocab={vocab} isLevelInherited={levelInherited} warnings={warnings.vocab} />
          <ObjectsSection
            objects={report.objects}
            recordingHours={recordingHours}
            isLevelInherited={levelInherited}
            warnings={warnings.objects}
          />
        </>
      );
    case "Absent":
      return (
        <>
          <VocabSection vocab={vocab} isLevelInherited={levelInherited} warnings={warnings.vocab} />
          <p className="mt-5 rounded-xl border border-outline bg-muted-surface/35 p-3 text-sm text-text-mid">
            {t("analysis_report_absent")}
          </p>
        </>
      );
    // A report stamped by a build of a different version ("Unrecognized").
    default:
      return (
        <>
          <VocabSection vocab={vocab} isLevelInherited={levelInherited} warnings={warnings.vocab} />
          <p className="mt-5 rounded-xl border border-outline bg-muted-surface/35 p-3 text-sm text-text-mid">
            {t("analysis_report_unrecognized")}
          </p>
        </>
      );
  }
};

type AnalysisReportTabProps = {
  recordingId: string;
  /** Which analysis run to report on; the newest completed one by default. */
  runId?: string;
  /**
   * The ids of the runs this recording still has. Only used to tell a resumed
   * run's base run apart from one that has since been deleted — the report
   * itself carries the id either way.
   */
  knownRunIds?: string[];
};

/**
 * Admin-only per-analysis diagnostics: vocabulary usage
 * (derived from the stored analysis), plus the run-generated report
 * (annotation stats, per-stage costs, full level stats) when available.
 */
const AnalysisReportTab = ({ recordingId, runId, knownRunIds = [] }: AnalysisReportTabProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const [data, setData] = useState<RecordingAnalysisReport | null>(null);
  // A flag, translated at render time: an error message in state would drag `t`
  // into the effect's dependencies, and a language switch would refetch the
  // whole report just to re-word the failure.
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    get_recording_analysis_report(recordingId, runId)
      .then(report => {
        if (!cancelled) setData(report);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recordingId, runId]);

  return (
    <div>
      {loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}
      {failed && <p className="text-sm text-danger">{t("failed_to_load_analysis_report")}</p>}
      {data && (
        <>
          <ProvenanceSection
            recordingId={recordingId}
            runId={data.run_id}
            provenance={data.report.version === "V1" ? data.report.provenance : []}
            resumedFrom={data.report.version === "V1" ? data.report.resumedFrom : null}
            knownRunIds={knownRunIds}
          />
          <ReportBody
            report={data.report}
            vocab={data.summary.vocab}
            recordingHours={data.summary.recordingHours}
          />
        </>
      )}
    </div>
  );
};

export default AnalysisReportTab;
